import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useCart, useCartActions } from '../../contexts/StoreContext'
import { useAccount } from '../../contexts/AccountContext'
import { useCatalogRefresh } from '../../contexts/CatalogContext'
import { useSubmit } from '../../hooks/useSubmit'
import { ApiError, submitOrder } from '../../utils/api'
import { isSoldOut } from '../../core/rules/stock'
import { today } from '../../core/format/dates'
import { fromCents, toCents } from '../../core/format/currency'
import { paymentFor } from '../../data/payments'
import type { Address, Country, OrderRequest, OrderResponse, PaymentMethod } from '../../core/types/requests'
import { addressFromAccount, addressKeys, emptyAddress, fieldId, isEmptyAddress, type AddressTextKey } from './checkoutRules'
import { useAddressLookup } from './useAddressLookup'
import { useVoucher } from './useVoucher'
import { formatPostcode } from '../../core/rules/contact'
import { attemptId, clearAttempt, readPlaced, savePending, savePlaced, type PlacedOrder } from './orderStorage'

export type { PlacedOrder } from './orderStorage'

/**
 * Alles achter de checkout: velden (vooraf ingevuld als ze ingelogd is), één id per
 * bestelpoging, uitverkochte regels blokkeren, focus naar de eerste fout of de bevestiging.
 */
export function useCheckout() {
  const cart = useCart()
  const { lines, total, gifts, appliedCode } = cart
  const { clearCart } = useCartActions()
  const { account, refresh } = useAccount()

  const [address, setAddress] = useState<Address>(() => (account ? addressFromAccount(account) : emptyAddress))
  // Altijd een methode die bij het land past: Wero alleen in NL, Bancontact alleen in BE.
  const [payment, setPayment] = useState<PaymentMethod>(() => paymentFor(account?.customer.payment.method ?? 'ideal', address.country))
  const [email, setEmail] = useState(account?.customer.email ?? '')
  const [skinNote, setSkinNote] = useState('')
  const [remind, setRemind] = useState(false)
  // Bewaard voor deze tab: ververst ze het bedankscherm, dan blijft de bevestiging staan
  // in plaats van "Je winkelmand is leeg". Een nieuwe mand wint altijd.
  const [placed, setPlacedState] = useState<PlacedOrder | null>(() => (lines.length ? null : readPlaced()))
  const setPlaced = (p: PlacedOrder) => {
    savePlaced(p)
    setPlacedState(p)
  }
  const refreshCatalog = useCatalogRefresh()
  const voucher = useVoucher(toCents(total))
  /**
   * Wijst de server het totaal af (409 `total_mismatch`), dan is deze poging voorbij: de volgende
   * krijgt een nieuwe id (de server onthoudt geen foutantwoord), en de prijzen en instellingen
   * worden stil ververst, zodat de mand het bedrag toont dat de server rekent. Zonder dat
   * verversen stuurde elke nieuwe poging hetzelfde foute bedrag.
   */
  const order = useSubmit(async (...args: Parameters<typeof submitOrder>) => {
    try {
      return await submitOrder(...args)
    } catch (err) {
      if (err instanceof ApiError && err.code === 'total_mismatch') {
        clearAttempt()
        void refreshCatalog()
      }
      // De bon is intussen gebruikt of verlopen: weghalen bij het veld, met de reden erbij.
      if (err instanceof ApiError && err.code?.startsWith('voucher_')) voucher.reject(err)
      throw err
    }
  })
  const errorRef = useRef<HTMLDivElement | null>(null)
  const doneRef = useRef<HTMLHeadingElement | null>(null)

  // Komt het account pas binnen na openen (verversen of direct op /afrekenen)? Dan alsnog
  // invullen — maar alleen als ze zelf nog niets heeft getypt.
  const prefilled = useRef(Boolean(account))
  useEffect(() => {
    if (!account || prefilled.current) return
    prefilled.current = true
    const next = isEmptyAddress(address) ? addressFromAccount(account) : address
    setAddress(next)
    setEmail((e) => e || account.customer.email)
    setPayment(paymentFor(account.customer.payment.method, next.country))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- eenmalig zodra het account binnenkomt
  }, [account])

  // Mislukt? Focus naar het eerste foute veld, anders naar de foutmelding — zodat
  // toetsenbord- en schermlezergebruikers direct horen wat er mis is.
  useEffect(() => {
    if (order.status !== 'error') return
    const firstField = addressKeys.find((k) => order.fieldErrors[k]) ?? (order.fieldErrors.email ? 'email' : null)
    const target = firstField ? document.getElementById(fieldId(firstField)) : errorRef.current
    target?.focus()
  }, [order.status, order.fieldErrors])

  // Geplaatst: focus naar de bevestiging.
  useEffect(() => {
    if (placed) doneRef.current?.focus()
  }, [placed])

  // Uitverkocht geraakt sinds het in de mand ging (voorraad komt live uit de catalogus),
  // of door de server afgewezen per regel (fieldErrors `line:<productId>`).
  const lineError = (id: string): string | undefined => order.fieldErrors[`line:${id}`]
  const lineProblem = (id: string, soldOut: boolean) => lineError(id) ?? (soldOut ? 'Net uitverkocht' : undefined)
  const blockedLines = lines.filter((l) => isSoldOut(l.product) || lineError(l.product.id))

  const setAddressField = (key: AddressTextKey, value: string) => setAddress((a) => ({ ...a, [key]: value }))
  // Nederland: postcode + huisnummer → straat en plaats (alleen lege of eerder aangevulde velden).
  const lookup = useAddressLookup(address, (s) => setAddress((a) => ({ ...a, street: s.street, city: s.city })))
  const setCountry = (country: Country) => {
    setAddress((a) => ({ ...a, country }))
    setPayment((p) => paymentFor(p, country))
  }

  /** De bevestiging zoals de server hem rekende: bedragen in centen uit het antwoord, nooit afgeleid. */
  const placedFrom = (result: OrderResponse): PlacedOrder => ({
    orderId: result.orderId,
    firstName: address.firstName.trim(),
    email: email.trim(),
    // Wat ze betaalde: na de cadeaubon.
    total: fromCents(result.totalCents - result.voucherCents),
    gift: result.gift,
    // Het bestelnummer bestaat pas na betalen; de terugkeerpagina vult het in vanuit de status.
    orderNumber: null,
    remind,
    accountOrder: {
      id: result.orderId,
      date: today(),
      lines: lines.map((l) => ({ productId: l.product.id, qty: l.qty, price: l.product.price })),
      shipping: fromCents(result.shippingCents),
      status: 'In behandeling',
      viaSubscription: lines.some((l) => l.subscribe),
    },
    subscribeIds: lines.filter((l) => l.subscribe).map((l) => l.product.id),
  })

  const placeOrder = async (e: FormEvent) => {
    e.preventDefault()
    if (blockedLines.length) return
    const orderLines = lines.map((l) => ({ productId: l.product.id, qty: l.qty, subscribe: l.subscribe, routineId: l.routineId, ...(l.via ? { via: l.via } : {}) }))
    const request: Omit<OrderRequest, 'requestId'> = {
      email: email.trim(),
      address: {
        ...address,
        street: address.street.trim(),
        houseNumber: address.houseNumber.trim(),
        city: address.city.trim(),
        postcode: formatPostcode(address.postcode),
      },
      lines: orderLines,
      paymentMethod: payment,
      code: appliedCode?.code ?? null,
      skinNote: skinNote.trim(),
      refillReminder: remind,
      giftChoices: Object.fromEntries(gifts.flatMap((g) => (g.choice ? [[g.tier.ruleId, g.choice]] : []))),
      ...(voucher.applied ? { voucherCode: voucher.applied.code } : {}),
      // Het bedrag ná de bon: dat is wat ze ziet en betaalt.
      expectedTotalCents: voucher.payableCents,
    }
    const giftPreview = gifts.map((g) => g.choice ?? g.tier.title).join(' + ') || null
    // Eén id per bestelpoging, bewaard buiten dit scherm: ook na wegklikken tijdens het versturen
    // en terugkomen (nieuwe mount) gaat dezelfde id mee, dus nooit een tweede order. De hele
    // aanvraag telt mee: een verbeterd adres of een andere code is een nieuwe poging.
    const requestId = attemptId(request)
    const result = await order.submit({ requestId, ...request }, { total, gift: giftPreview })
    if (!result) return
    const confirmation = placedFrom(result)
    // Met een echte backend: door naar Mollie. De poging-id blijft bewust staan: komt ze met
    // "terug" weer hier en drukt ze opnieuw, dan herkent de server dezelfde poging en geeft
    // dezelfde betaallink. De mand blijft tot de terugkeerpagina (/bestelling/:id) de betaling
    // bevestigt; die leest daarvoor de bevestiging die we hier klaarzetten.
    if (result.paymentUrl) {
      savePending(confirmation)
      window.location.assign(result.paymentUrl)
      return
    }
    clearAttempt()
    setPlaced(confirmation)
    clearCart()
    // Ingelogd: de nieuwe bestelling staat meteen in Mijn omgeving.
    if (account) void refresh()
    window.scrollTo({ top: 0 })
  }

  return {
    cart,
    account,
    email,
    setEmail,
    address,
    setAddressField,
    setCountry,
    lookup,
    payment,
    setPayment,
    skinNote,
    setSkinNote,
    remind,
    setRemind,
    voucher,
    placeOrder,
    placed,
    blockedLines,
    lineProblem,
    submitting: order.submitting,
    error: order.error,
    fieldErrors: order.fieldErrors,
    errorRef,
    doneRef,
  }
}

export type CheckoutControl = ReturnType<typeof useCheckout>
